import React, { useState, useEffect } from 'react';
import { Layout } from '../components/layout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { Award, Lock, ChevronDown, ChevronUp, Star, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

const BadgeLegend = () => {
  const [categories, setCategories] = useState([]);
  const [badges, setBadges] = useState([]);
  const [myBadges, setMyBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  const { user } = useAuth();

  useEffect(() => {
    fetchBadges();
  }, [user]);

  const fetchBadges = async () => {
    try {
      const [legendRes, myBadgesRes] = await Promise.all([
        api.get('/badges/legend-organized'),
        user ? api.get('/badges/my-badges') : Promise.resolve({ data: { badges: [] } })
      ]);

      const organizedData = legendRes.data.organized || legendRes.data;
      const allBadgesFromCategories = [];
      
      const categoryList = Object.keys(organizedData).map(categoryKey => {
        const categoryData = organizedData[categoryKey];
        const categoryBadges = categoryData.badges || [];
        allBadgesFromCategories.push(...categoryBadges);
        
        return {
          id: categoryKey,
          name: categoryData.category?.name || categoryKey,
          badges: categoryBadges,
          count: categoryData.count || categoryBadges.length
        };
      });

      setCategories(categoryList);
      setBadges(allBadgesFromCategories);
      setMyBadges(myBadgesRes.data.badges || []);
      
      // Expand all categories by default
      const expanded = {};
      categoryList.forEach(cat => {
        expanded[cat.id] = true;
      });
      setExpandedCategories(expanded);
    } catch (error) {
      console.error('Error fetching badges:', error);
      toast.error('Failed to load badge legend');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const hasBadge = (badgeId) => {
    return myBadges.some(mb => mb.badge_id === badgeId);
  };

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'official': return 'text-purple-400 border-purple-500/50';
      case 'special': return 'text-yellow-400 border-yellow-500/50';
      case 'legacy': return 'text-blue-400 border-blue-500/50';
      default: return 'text-gray-400 border-gray-700';
    }
  };

  const getRarityLabel = (rarity) => {
    switch (rarity) {
      case 'official': return '👑 Official';
      case 'special': return '⭐ Special';
      case 'legacy': return '🏛️ Legacy';
      default: return 'Standard';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-black">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C5A52A] mx-auto mb-4"></div>
            <p className="font-mono text-sm text-gray-500">Loading badge legend...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Sparkles className="w-8 h-8 text-[#C5A52A]" />
              <h1 className="font-headline text-5xl md:text-6xl text-white">
                BADGE LEGEND
              </h1>
              <Sparkles className="w-8 h-8 text-[#C5A52A]" />
            </div>
            <p className="font-mono text-sm text-gray-400 max-w-2xl mx-auto mb-6">
              Discover all available badges, how to earn them, and track your collection progress.
            </p>
            
            {user && myBadges.length > 0 && (
              <div className="inline-flex items-center gap-2 bg-[#C5A52A]/10 border border-[#C5A52A]/30 rounded-full px-6 py-3">
                <Award className="w-5 h-5 text-[#C5A52A]" />
                <span className="font-mono text-sm text-white">
                  You've earned <strong className="text-[#C5A52A]">{myBadges.length}</strong> badge{myBadges.length !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>

          {/* Quick Stats */}
          {user && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-4 text-center">
                <div className="text-2xl mb-2">🏆</div>
                <div className="font-headline text-2xl text-white mb-1">{myBadges.length}</div>
                <div className="font-mono text-xs text-gray-500">Earned</div>
              </div>
              <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-4 text-center">
                <div className="text-2xl mb-2">🔒</div>
                <div className="font-headline text-2xl text-white mb-1">
                  {badges.filter(b => b.is_public).length - myBadges.length}
                </div>
                <div className="font-mono text-xs text-gray-500">Locked</div>
              </div>
              <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-4 text-center">
                <div className="text-2xl mb-2">📊</div>
                <div className="font-headline text-2xl text-white mb-1">
                  {Math.round((myBadges.length / badges.filter(b => b.is_public).length) * 100) || 0}%
                </div>
                <div className="font-mono text-xs text-gray-500">Complete</div>
              </div>
              <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-4 text-center">
                <div className="text-2xl mb-2">⭐</div>
                <div className="font-headline text-2xl text-white mb-1">
                  {myBadges.filter(mb => {
                    const badge = badges.find(b => b.id === mb.badge_id);
                    return badge && badge.rarity === 'special';
                  }).length}
                </div>
                <div className="font-mono text-xs text-gray-500">Special</div>
              </div>
            </div>
          )}

          {/* Badge Categories */}
          <div className="space-y-6">
            {categories.map((category) => {
              const categoryBadges = category.badges || [];
              const earnedCount = categoryBadges.filter(b => hasBadge(b.id)).length;
              const isExpanded = expandedCategories[category.id];

              return (
                <div key={category.id} className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg overflow-hidden">
                  {/* Category Header */}
                  <button
                    onClick={() => toggleCategory(category.id)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-[#111] transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <h3 className="font-headline text-xl text-white">{category.name}</h3>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-gray-500">
                          {earnedCount}/{categoryBadges.length}
                        </span>
                        {user && earnedCount > 0 && (
                          <div className="w-24 h-2 bg-gray-800 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-[#C5A52A] transition-all"
                              style={{ width: `${(earnedCount / categoryBadges.length) * 100}%` }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </button>

                  {/* Category Badges */}
                  {isExpanded && (
                    <div className="px-6 pb-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {categoryBadges.map((badge) => {
                          const earned = hasBadge(badge.id);
                          const isLocked = !earned;

                          return (
                            <div
                              key={badge.id}
                              className={`bg-[#111] border rounded-lg p-4 transition-all ${
                                earned
                                  ? 'border-green-500/50 bg-green-900/10'
                                  : `border-gray-800 hover:border-gray-700`
                              }`}
                            >
                              {/* Badge Icon with Lock Overlay */}
                              <div className="flex items-start gap-4 mb-3">
                                <div className="relative flex-shrink-0">
                                  {badge.icon_url && badge.icon_url.startsWith('http') ? (
                                    <img
                                      src={badge.icon_url}
                                      alt={badge.name}
                                      className={`w-16 h-16 object-contain rounded transition-all duration-300 ${
                                        isLocked ? 'grayscale opacity-40' : ''
                                      }`}
                                    />
                                  ) : (
                                    <div
                                      className={`text-5xl transition-all duration-300 ${
                                        isLocked ? 'grayscale opacity-40' : ''
                                      }`}
                                    >
                                      {badge.icon_url || '🏅'}
                                    </div>
                                  )}
                                  {isLocked && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                      <Lock className="w-6 h-6 text-gray-400" />
                                    </div>
                                  )}
                                </div>

                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2 mb-1">
                                    <h4 className="font-mono text-sm text-white font-bold">
                                      {badge.name}
                                    </h4>
                                    {earned && (
                                      <Award className="w-4 h-4 text-green-400 flex-shrink-0" />
                                    )}
                                  </div>
                                  
                                  {/* Rarity Badge */}
                                  {badge.rarity !== 'standard' && (
                                    <div className={`inline-block px-2 py-0.5 rounded text-xs font-mono mb-2 border ${
                                      getRarityColor(badge.rarity)
                                    }`}>
                                      {getRarityLabel(badge.rarity)}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Description */}
                              {badge.description && (
                                <p className="font-mono text-xs text-gray-400 mb-3 line-clamp-2">
                                  {badge.description}
                                </p>
                              )}

                              {/* Requirements */}
                              <div className="space-y-2">
                                {badge.badge_type === 'location' && badge.venue_name && (
                                  <div className="flex items-center gap-2 text-xs">
                                    <span className="text-gray-600">📍</span>
                                    <span className="font-mono text-gray-500">
                                      Check in at {badge.venue_name}
                                    </span>
                                  </div>
                                )}

                                {badge.badge_type === 'activity' && (
                                  <div className="space-y-1">
                                    {badge.required_posts_count && (
                                      <div className="flex items-center gap-2 text-xs">
                                        <span className="text-gray-600">📝</span>
                                        <span className="font-mono text-gray-500">
                                          Create {badge.required_posts_count} posts
                                        </span>
                                      </div>
                                    )}
                                    {badge.required_comments_count && (
                                      <div className="flex items-center gap-2 text-xs">
                                        <span className="text-gray-600">💬</span>
                                        <span className="font-mono text-gray-500">
                                          Write {badge.required_comments_count} comments
                                        </span>
                                      </div>
                                    )}
                                    {badge.required_login_days && (
                                      <div className="flex items-center gap-2 text-xs">
                                        <span className="text-gray-600">📅</span>
                                        <span className="font-mono text-gray-500">
                                          Active for {badge.required_login_days} days
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {badge.badge_type === 'collection' && badge.required_badge_ids && badge.required_badge_ids.length > 0 && (
                                  <div className="flex items-center gap-2 text-xs">
                                    <span className="text-gray-600">🏆</span>
                                    <span className="font-mono text-gray-500">
                                      Collect {badge.required_badge_ids.length} badges
                                    </span>
                                  </div>
                                )}

                                {/* Scarcity */}
                                {badge.max_supply && badge.show_scarcity && (
                                  <div className="flex items-center gap-2 text-xs">
                                    <span className="text-yellow-500">⚡</span>
                                    <span className="font-mono text-yellow-400">
                                      {badge.max_supply - (badge.current_supply || 0)}/{badge.max_supply} remaining
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Empty State */}
          {categories.length === 0 && (
            <div className="text-center py-12">
              <Award className="w-16 h-16 text-gray-700 mx-auto mb-4" />
              <p className="font-mono text-sm text-gray-500">
                No badges available yet. Check back soon!
              </p>
            </div>
          )}

          {/* CTA for Non-Logged Users */}
          {!user && (
            <div className="mt-12 text-center bg-gradient-to-r from-[#C5A52A]/10 to-transparent border border-[#C5A52A]/30 rounded-lg p-8">
              <h3 className="font-headline text-2xl text-white mb-4">Start Your Collection</h3>
              <p className="font-mono text-sm text-gray-400 mb-6">
                Create an account to start earning badges and track your progress!
              </p>
              <div className="flex gap-4 justify-center">
                <Link
                  to="/register"
                  className="bg-[#C5A52A] hover:bg-[#D4B93A] text-black px-8 py-3 rounded font-mono text-sm uppercase tracking-wider transition-colors"
                >
                  Sign Up
                </Link>
                <Link
                  to="/login"
                  className="bg-transparent border border-[#C5A52A] hover:bg-[#C5A52A]/10 text-[#C5A52A] px-8 py-3 rounded font-mono text-sm uppercase tracking-wider transition-colors"
                >
                  Log In
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default BadgeLegend;
